// components/tier-branch/BranchSettlementDialog.tsx
// Branch balance settlement (admin ⇄ super admin). Shows the branch P&L
// ("wager"), a drill-down of every deposit/withdraw, the settlement history,
// and lets the super admin record a settlement + download a CSV report.
import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Card, Typography,
  Button, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody, Chip,
  Stack, TextField, MenuItem, CircularProgress, Divider, Pagination,
} from "@mui/material";
import { Download, AccountBalance, Warning } from "@mui/icons-material";
import type { Branch } from "../../types/tierBranch";
import * as api from "../../services/tierBranchApi";
import type { SettlementSummary, BranchTxn, Settlement } from "../../services/tierBranchApi";
import { fmt } from "./useSnack";
import type { Notify } from "./useSnack";

const Stat: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Card sx={{ p: 1.5, minWidth: 150, flex: 1 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" fontWeight={700} sx={{ color: color || "text.primary" }}>{value}</Typography>
  </Card>
);

const txnStatusColor = (s: string): any =>
  ["approved", "Done"].includes(s) ? "success" : ["rejected", "Rejected"].includes(s) ? "error" : "warning";

const BranchSettlementDialog: React.FC<{
  open: boolean;
  branch: Branch | null;
  onClose: () => void;
  notify: Notify;
}> = ({ open, branch, onClose, notify }) => {
  const [tab, setTab] = useState(0);
  const [sum, setSum] = useState<SettlementSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const [txns, setTxns] = useState<BranchTxn[]>([]);
  const [txnType, setTxnType] = useState("all");
  const [txnPage, setTxnPage] = useState(1);
  const [txnPages, setTxnPages] = useState(1);

  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [form, setForm] = useState({ direction: "collect" as "collect" | "payout", amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const id = branch?.id;

  const loadSummary = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try { setSum(await api.getBranchSettlement(id)); }
    catch (e: any) { notify(e.message || "Failed to load", "error"); }
    finally { setLoading(false); }
  }, [id, notify]);

  const loadTxns = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api.getBranchTransactions(id, { type: txnType, page: txnPage, limit: 15 });
      setTxns(r.data); setTxnPages(r.totalPages);
    } catch (e: any) { notify(e.message || "Failed", "error"); }
  }, [id, txnType, txnPage, notify]);

  const loadSettlements = useCallback(async () => {
    if (!id) return;
    try { setSettlements((await api.getBranchSettlements(id, { limit: 100 })).data); }
    catch (e: any) { notify(e.message || "Failed", "error"); }
  }, [id, notify]);

  useEffect(() => { if (open && id) { setTab(0); setTxnPage(1); loadSummary(); loadSettlements(); } }, [open, id, loadSummary, loadSettlements]);
  useEffect(() => { if (open && id) loadTxns(); }, [open, id, loadTxns]);

  const settle = async () => {
    if (!id) return;
    const amount = Number(form.amount);
    if (!(amount > 0)) { notify("Enter a positive amount", "error"); return; }
    setSaving(true);
    try {
      const r = await api.createBranchSettlement(id, { amount, direction: form.direction, note: form.note });
      setSum(r.balance);
      setForm({ direction: "collect", amount: "", note: "" });
      await loadSettlements();
      notify("Settlement recorded", "success");
    } catch (e: any) { notify(e.message || "Settlement failed", "error"); }
    finally { setSaving(false); }
  };

  const download = async () => {
    if (!id) return;
    try { await api.downloadSettlementReport(id, branch?.name); }
    catch (e: any) { notify(e.message || "Download failed", "error"); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountBalance fontSize="small" />
          <span>Settlement · {branch?.name}</span>
          {sum?.overLimit && <Chip size="small" color="error" icon={<Warning />} label="Over credit limit" />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading || !sum ? (
          <Box sx={{ textAlign: "center", py: 5 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* P&L summary */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 1, flexWrap: "wrap", gap: 1.5 }}>
              <Stat label="Gross Deposits" value={fmt(sum.grossDeposits)} color="#0ECC68" />
              <Stat label="Gross Withdrawals" value={fmt(sum.grossWithdrawals)} color="#E01B4F" />
              <Stat label="Net P&L (Wager)" value={fmt(sum.netPnL)} color={sum.netPnL >= 0 ? "#0ECC68" : "#E01B4F"} />
              <Stat label="Outstanding to settle" value={fmt(sum.outstanding)} color="#FFC23F" />
              <Stat label="Credit Limit" value={fmt(sum.creditLimit)} color={sum.overLimit ? "#E01B4F" : undefined} />
            </Stack>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
              <Tab label="Transactions" />
              <Tab label={`Settlements (${settlements.length})`} />
            </Tabs>

            {tab === 0 && (
              <Box>
                <TextField select size="small" label="Type" value={txnType}
                  onChange={(e) => { setTxnPage(1); setTxnType(e.target.value); }} sx={{ minWidth: 160, mb: 1 }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="deposit">Deposits</MenuItem>
                  <MenuItem value="withdraw">Withdrawals</MenuItem>
                </TextField>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Type", "User", "Amount", "Status", "Date", "Action By", "Remark"].map((h) => (
                          <TableCell key={h} sx={{ color: "text.secondary" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {txns.map((t) => (
                        <TableRow key={`${t.kind}-${t.id}`} hover>
                          <TableCell><Chip size="small" variant="outlined" color={t.kind === "deposit" ? "success" : "error"} label={t.kind} /></TableCell>
                          <TableCell>{t.username}</TableCell>
                          <TableCell>{fmt(t.amount)}</TableCell>
                          <TableCell><Chip size="small" color={txnStatusColor(t.status)} label={t.status} /></TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{t.at ? new Date(t.at).toLocaleString() : "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{t.actionBy || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{t.remark || "—"}</TableCell>
                        </TableRow>
                      ))}
                      {!txns.length && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>No transactions.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </Box>
                {txnPages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                    <Pagination size="small" count={txnPages} page={txnPage} onChange={(_, p) => setTxnPage(p)} />
                  </Box>
                )}
              </Box>
            )}

            {tab === 1 && (
              <Box>
                {/* record a settlement */}
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Record a settlement</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
                    <TextField select size="small" label="Direction" value={form.direction} sx={{ minWidth: 200 }}
                      onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value as any }))}>
                      <MenuItem value="collect">Collect from branch</MenuItem>
                      <MenuItem value="payout">Pay out to branch</MenuItem>
                    </TextField>
                    <TextField size="small" type="number" label="Amount" value={form.amount}
                      onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
                    <Button size="small" onClick={() => setForm((f) => ({ ...f, amount: String(Math.abs(sum.outstanding)) }))}>
                      Use outstanding ({fmt(Math.abs(sum.outstanding))})
                    </Button>
                    <TextField size="small" label="Note" value={form.note} sx={{ flex: 1, minWidth: 160 }}
                      onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                    <Button variant="contained" onClick={settle} disabled={saving || !(Number(form.amount) > 0)}>
                      {saving ? "Saving…" : "Settle"}
                    </Button>
                  </Stack>
                </Card>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">History</Typography>
                  <Button size="small" startIcon={<Download />} onClick={download} disabled={!settlements.length}>Download CSV</Button>
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Date", "Direction", "Amount", "Outstanding →", "By", "Note"].map((h) => (
                          <TableCell key={h} sx={{ color: "text.secondary" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {settlements.map((s) => (
                        <TableRow key={s.id} hover>
                          <TableCell sx={{ fontSize: 12 }}>{new Date(s.createdAt).toLocaleString()}</TableCell>
                          <TableCell><Chip size="small" color={s.direction === "collect" ? "info" : "warning"} label={s.direction} /></TableCell>
                          <TableCell>{fmt(s.amount)}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{fmt(s.outstandingBefore)} → {fmt(s.outstandingAfter)}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{s.settledByName || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{s.note || "—"}</TableCell>
                        </TableRow>
                      ))}
                      {!settlements.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>No settlements yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchSettlementDialog;
